import { calculateDistance as haversineDistance, ZIMBABWE_BOUNDS as GEO_ZIMBABWE_BOUNDS } from '../utils/geo';

/**
 * Location Service
 * Handles GPS validation, location verification, and anti-spoofing measures
 */

const LOCATION_LOGS_KEY = 'buildzim_location_logs';
const TRUSTED_LOCATIONS_KEY = 'buildzim_trusted_locations';

// Utility functions
const getLocationLogs = () => {
  const logs = localStorage.getItem(LOCATION_LOGS_KEY);
  return logs ? JSON.parse(logs) : [];
};

const saveLocationLogs = (logs) => {
  localStorage.setItem(LOCATION_LOGS_KEY, JSON.stringify(logs));
};

const getTrustedLocations = () => {
  const locations = localStorage.getItem(TRUSTED_LOCATIONS_KEY);
  return locations ? JSON.parse(locations) : [];
};

const saveTrustedLocations = (locations) => {
  localStorage.setItem(TRUSTED_LOCATIONS_KEY, JSON.stringify(locations));
};

// Simulate network delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Distance calculation delegates to shared geo utility
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  return haversineDistance(lat1, lon1, lat2, lon2);
};

// Zimbabwe coordinates bounds for validation (from shared geo utility)
const ZIMBABWE_BOUNDS = GEO_ZIMBABWE_BOUNDS;

// Major cities in Zimbabwe with approximate coordinates
const MAJOR_CITIES = {
  'Harare': { lat: -17.8252, lng: 31.0335, radius: 50 },
  'Bulawayo': { lat: -20.1619, lng: 28.5810, radius: 30 },
  'Chitungwiza': { lat: -18.0086, lng: 31.0614, radius: 15 },
  'Mutare': { lat: -18.9707, lng: 32.6473, radius: 20 },
  'Gweru': { lat: -19.4500, lng: 29.8167, radius: 15 },
  'Kwekwe': { lat: -18.9167, lng: 29.8167, radius: 10 },
  'Kadoma': { lat: -18.3333, lng: 29.9167, radius: 10 },
  'Masvingo': { lat: -20.0638, lng: 30.8276, radius: 15 },
  'Chinhoyi': { lat: -17.3667, lng: 30.2000, radius: 10 },
  'Marondera': { lat: -18.1851, lng: 31.5511, radius: 10 }
};

// Initialize sample data
const initializeSampleData = () => {
  const existingTrusted = getTrustedLocations();
  if (existingTrusted.length === 0) {
    const sampleTrustedLocations = [
      {
        id: 'TL_001',
        projectId: 'PROJ-1000',
        name: 'Borrowdale Villa Site',
        coordinates: {
          lat: -17.8052,
          lng: 31.0735,
          accuracy: 5
        },
        address: 'Plot 15, Borrowdale Brook, Harare',
        verifiedBy: 'VER_001',
        verifiedAt: '2024-01-15T08:00:00Z',
        radius: 100, // meters
        status: 'active'
      },
      {
        id: 'TL_002', 
        projectId: 'PROJ-1001',
        name: 'Avondale Boundary Wall Site',
        coordinates: {
          lat: -17.7964,
          lng: 31.0486,
          accuracy: 3
        },
        address: '45 Avondale Road, Avondale, Harare',
        verifiedBy: 'VER_001',
        verifiedAt: '2024-02-01T09:00:00Z',
        radius: 75,
        status: 'active'
      }
    ];
    saveTrustedLocations(sampleTrustedLocations);
  }
};

// Location service
const locationService = {
  
  // Initialize service
  initialize: () => {
    initializeSampleData();
  },

  // Get current location using browser API
  getCurrentLocation: async (options = {}) => {
    await delay(200);
    
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // 1 minute
        ...options
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: new Date(position.timestamp).toISOString(),
            source: 'gps'
          };
          
          resolve({
            success: true,
            data: locationData,
            message: 'Location retrieved successfully'
          });
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          reject(new Error(errorMessage));
        },
        defaultOptions
      );
    });
  },

  // Validate location within Zimbabwe bounds
  validateLocationBounds: async (lat, lng) => {
    await delay(100);
    
    const isWithinBounds = (
      lat >= ZIMBABWE_BOUNDS.south &&
      lat <= ZIMBABWE_BOUNDS.north &&
      lng >= ZIMBABWE_BOUNDS.west &&
      lng <= ZIMBABWE_BOUNDS.east
    );
    
    return {
      valid: isWithinBounds,
      bounds: ZIMBABWE_BOUNDS,
      message: isWithinBounds ? 'Location is within Zimbabwe' : 'Location is outside Zimbabwe'
    };
  },

  // Validate location against project site
  validateProjectLocation: async (lat, lng, projectId, toleranceRadius = 100) => {
    await delay(300);
    
    const trustedLocations = getTrustedLocations();
    const projectLocation = trustedLocations.find(tl => tl.projectId === projectId);
    
    if (!projectLocation) {
      return {
        valid: false,
        distance: null,
        message: 'No trusted location found for this project',
        requiresSetup: true
      };
    }
    
    const distance = calculateDistance(
      lat, lng,
      projectLocation.coordinates.lat,
      projectLocation.coordinates.lng
    ) * 1000; // Convert to meters
    
    const isValid = distance <= toleranceRadius;
    
    return {
      valid: isValid,
      distance: Math.round(distance),
      toleranceRadius: toleranceRadius,
      trustedLocation: projectLocation,
      message: isValid 
        ? `Location verified (${Math.round(distance)}m from project site)`
        : `Location too far from project site (${Math.round(distance)}m away, max allowed: ${toleranceRadius}m)`
    };
  },

  // Anti-spoofing validation
  validateLocationAuthenticity: async (locationData, previousLocations = []) => {
    await delay(400);
    
    const validationResults = {
      accuracyValid: true,
      speedValid: true,
      continuityValid: true,
      deviceConsistent: true,
      timeValid: true,
      overallScore: 0,
      warnings: [],
      errors: []
    };
    
    // Check location accuracy
    if (locationData.accuracy > 50) {
      validationResults.accuracyValid = false;
      validationResults.warnings.push('Location accuracy is low (>50m)');
    }
    
    // Check against previous locations for continuity
    if (previousLocations.length > 0) {
      const lastLocation = previousLocations[previousLocations.length - 1];
      const timeDiff = (new Date(locationData.timestamp) - new Date(lastLocation.timestamp)) / 1000; // seconds
      const distance = calculateDistance(
        locationData.lat, locationData.lng,
        lastLocation.lat, lastLocation.lng
      ) * 1000; // meters
      
      // Check for impossible movement speed (faster than 200 km/h)
      if (timeDiff > 0) {
        const speed = (distance / timeDiff) * 3.6; // km/h
        if (speed > 200) {
          validationResults.speedValid = false;
          validationResults.errors.push(`Impossible movement speed detected: ${Math.round(speed)} km/h`);
        }
      }
      
      // Check for teleportation (large distance in short time)
      if (distance > 1000 && timeDiff < 60) {
        validationResults.continuityValid = false;
        validationResults.errors.push('Possible location spoofing: large distance change in short time');
      }
    }
    
    // Check timestamp validity
    const now = new Date();
    const locationTime = new Date(locationData.timestamp);
    const timeDiff = Math.abs(now - locationTime) / 1000; // seconds
    
    if (timeDiff > 300) { // More than 5 minutes difference
      validationResults.timeValid = false;
      validationResults.warnings.push('Location timestamp is not current');
    }
    
    // Calculate overall validation score
    let score = 100;
    if (!validationResults.accuracyValid) score -= 15;
    if (!validationResults.speedValid) score -= 30;
    if (!validationResults.continuityValid) score -= 40;
    if (!validationResults.deviceConsistent) score -= 10;
    if (!validationResults.timeValid) score -= 5;
    
    validationResults.overallScore = Math.max(0, score);
    
    return {
      success: true,
      data: validationResults,
      message: `Location authenticity validation completed (Score: ${validationResults.overallScore}/100)`
    };
  },

  // Log location for audit trail
  logLocation: async (locationData, context = {}) => {
    await delay(200);
    
    const logEntry = {
      id: `LOG_${Date.now()}`,
      locationData: locationData,
      context: context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      platform: navigator.platform
    };
    
    const logs = getLocationLogs();
    logs.push(logEntry);
    
    // Keep only last 1000 logs to prevent storage bloat
    if (logs.length > 1000) {
      logs.splice(0, logs.length - 1000);
    }
    
    saveLocationLogs(logs);
    
    return {
      success: true,
      data: logEntry,
      message: 'Location logged successfully'
    };
  },

  // Create trusted location for project
  createTrustedLocation: async (projectId, locationData, verifierId) => {
    await delay(400);
    
    const trustedLocations = getTrustedLocations();
    
    // Check if trusted location already exists for this project
    const existingLocation = trustedLocations.find(tl => tl.projectId === projectId);
    if (existingLocation) {
      throw new Error('Trusted location already exists for this project');
    }
    
    const trustedLocation = {
      id: `TL_${Date.now()}`,
      projectId: projectId,
      name: locationData.name || `Project ${projectId} Site`,
      coordinates: {
        lat: locationData.lat,
        lng: locationData.lng,
        accuracy: locationData.accuracy || 5
      },
      address: locationData.address || 'Address not provided',
      verifiedBy: verifierId,
      verifiedAt: new Date().toISOString(),
      radius: locationData.radius || 100,
      status: 'active'
    };
    
    trustedLocations.push(trustedLocation);
    saveTrustedLocations(trustedLocations);
    
    return {
      success: true,
      data: trustedLocation,
      message: 'Trusted location created successfully'
    };
  },

  // Get trusted locations
  getTrustedLocations: async (projectId = null) => {
    await delay(200);
    
    let locations = getTrustedLocations();
    
    if (projectId) {
      locations = locations.filter(tl => tl.projectId === projectId);
    }
    
    return locations;
  },

  // Update trusted location
  updateTrustedLocation: async (locationId, updateData) => {
    await delay(300);
    
    const trustedLocations = getTrustedLocations();
    const locationIndex = trustedLocations.findIndex(tl => tl.id === locationId);
    
    if (locationIndex === -1) {
      throw new Error('Trusted location not found');
    }
    
    trustedLocations[locationIndex] = {
      ...trustedLocations[locationIndex],
      ...updateData,
      updatedAt: new Date().toISOString()
    };
    
    saveTrustedLocations(trustedLocations);
    
    return {
      success: true,
      data: trustedLocations[locationIndex],
      message: 'Trusted location updated successfully'
    };
  },

  // Get location history for analysis
  getLocationHistory: async (filters = {}) => {
    await delay(300);
    
    let logs = getLocationLogs();
    
    // Apply filters
    if (filters.verifierId) {
      logs = logs.filter(log => log.context.verifierId === filters.verifierId);
    }
    
    if (filters.projectId) {
      logs = logs.filter(log => log.context.projectId === filters.projectId);
    }
    
    if (filters.dateFrom) {
      logs = logs.filter(log => new Date(log.timestamp) >= new Date(filters.dateFrom));
    }
    
    if (filters.dateTo) {
      logs = logs.filter(log => new Date(log.timestamp) <= new Date(filters.dateTo));
    }
    
    return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  },

  // Find nearest city
  findNearestCity: async (lat, lng) => {
    await delay(200);
    
    let nearestCity = null;
    let shortestDistance = Infinity;
    
    Object.entries(MAJOR_CITIES).forEach(([cityName, cityData]) => {
      const distance = calculateDistance(lat, lng, cityData.lat, cityData.lng);
      if (distance < shortestDistance) {
        shortestDistance = distance;
        nearestCity = {
          name: cityName,
          distance: Math.round(distance * 100) / 100, // Round to 2 decimal places
          coordinates: cityData,
          withinRadius: distance <= (cityData.radius || 20)
        };
      }
    });
    
    return {
      success: true,
      data: nearestCity,
      message: nearestCity 
        ? `Nearest city: ${nearestCity.name} (${nearestCity.distance}km away)`
        : 'No nearby cities found'
    };
  },

  // Comprehensive location validation for verifiers
  validateVerifierLocation: async (locationData, projectId, verifierId) => {
    await delay(600);
    
    const validationResults = {
      boundsValid: false,
      projectLocationValid: false,
      authenticityValid: false,
      nearestCity: null,
      overallValid: false,
      score: 0,
      warnings: [],
      errors: []
    };
    
    try {
      // 1. Validate Zimbabwe bounds
      const boundsValidation = await locationService.validateLocationBounds(
        locationData.lat, locationData.lng
      );
      validationResults.boundsValid = boundsValidation.valid;
      if (!boundsValidation.valid) {
        validationResults.errors.push('Location is outside Zimbabwe');
      }
      
      // 2. Validate against project location
      const projectValidation = await locationService.validateProjectLocation(
        locationData.lat, locationData.lng, projectId
      );
      validationResults.projectLocationValid = projectValidation.valid;
      if (!projectValidation.valid) {
        validationResults.errors.push(projectValidation.message);
      }
      
      // 3. Validate authenticity
      const authenticityValidation = await locationService.validateLocationAuthenticity(locationData);
      validationResults.authenticityValid = authenticityValidation.data.overallScore >= 70;
      validationResults.warnings.push(...authenticityValidation.data.warnings);
      validationResults.errors.push(...authenticityValidation.data.errors);
      
      // 4. Find nearest city
      const cityResult = await locationService.findNearestCity(locationData.lat, locationData.lng);
      validationResults.nearestCity = cityResult.data;
      
      // 5. Calculate overall score
      let score = 0;
      if (validationResults.boundsValid) score += 20;
      if (validationResults.projectLocationValid) score += 50;
      if (validationResults.authenticityValid) score += 30;
      
      validationResults.score = score;
      validationResults.overallValid = score >= 70;
      
      // 6. Log location for audit
      await locationService.logLocation(locationData, {
        projectId,
        verifierId,
        validationScore: score
      });
      
    } catch (error) {
      validationResults.errors.push(error.message);
    }
    
    return {
      success: true,
      data: validationResults,
      message: `Location validation completed (Score: ${validationResults.score}/100)`
    };
  },

  // Generate location report
  generateLocationReport: async (projectId, dateRange = '30d') => {
    await delay(500);
    
    const endDate = new Date();
    const startDate = new Date();
    
    switch (dateRange) {
      case '7d':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(startDate.getDate() - 90);
        break;
    }
    
    const locationHistory = await locationService.getLocationHistory({
      projectId,
      dateFrom: startDate.toISOString(),
      dateTo: endDate.toISOString()
    });
    
    const trustedLocations = await locationService.getTrustedLocations(projectId);
    
    const report = {
      projectId,
      dateRange,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      totalLocationLogs: locationHistory.length,
      trustedLocations: trustedLocations.length,
      averageAccuracy: locationHistory.reduce((sum, log) => 
        sum + (log.locationData.accuracy || 0), 0) / locationHistory.length || 0,
      validationStats: {
        totalValidations: locationHistory.filter(log => log.context.validationScore).length,
        averageScore: locationHistory.reduce((sum, log) => 
          sum + (log.context.validationScore || 0), 0) / locationHistory.length || 0,
        passedValidations: locationHistory.filter(log => 
          log.context.validationScore >= 70).length
      },
      uniqueVerifiers: [...new Set(locationHistory.map(log => log.context.verifierId))].length,
      locationDistribution: locationHistory.reduce((acc, log) => {
        const city = 'Unknown'; // Would be determined from coordinates
        acc[city] = (acc[city] || 0) + 1;
        return acc;
      }, {}),
      generatedAt: new Date().toISOString()
    };
    
    return {
      success: true,
      data: report,
      message: 'Location report generated successfully'
    };
  }
};

// Initialize sample data when service is loaded
locationService.initialize();

export default locationService;